'use client';

import classnames from 'classnames';
import Box from '../../../atoms/Box/Box';
import Typography from '../../../atoms/Typography/Typography';
import type { GroupField, FormField } from '../types';
import type { FieldValues } from 'react-hook-form';
import { getEffectiveWidth } from '../utils';
import styles from '../Form.module.scss';

export type GroupControlProps = {
  field: GroupField;
  theme: 'dark' | 'light';
  watchedValues: FieldValues;
  renderField: (field: FormField, index: number) => React.ReactNode;
};

/**
 * Form control for grouped fields with optional title and description
 * @component
 * @param {GroupControlProps} props - Component props
 * @returns {JSX.Element} Group control component
 */
const GroupControl = ({ field, theme, watchedValues, renderField }: GroupControlProps) => {
  const isFlat = field.variant === 'flat';

  return (
    <Box
      key={`form_field_group_${field.id}`}
      className={classnames(styles.groupContainer, { [styles.groupContainerFlat]: isFlat })}
      data-theme={theme}
    >
      {(field.title || field.description) && (
        <Box className={styles.groupHeader}>
          {field.title && (
            <Typography
              variant="subtitle"
              size="small"
              className={styles.groupTitle}
              color={theme === 'dark' ? 'black' : 'white'}
              theme={theme}
            >
              {field.title}
            </Typography>
          )}
          {field.description && (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="gray-5"
              className={styles.groupDescription}
            >
              {field.description}
            </Typography>
          )}
        </Box>
      )}
      <Box className={styles.groupGrid}>
        {field.children.map((childField, childIndex) => {
          const effectiveWidth = getEffectiveWidth({
            field: childField,
            watchedValues,
          });
          return (
            <Box key={childField.id} className={classnames(styles.wrapper, styles[effectiveWidth])}>
              {renderField(childField as FormField, childIndex)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GroupControl;
